import {
  convertToApplication,
  convertToAvailableApplicationList,
} from 'models/Application/transfer';
import { Application } from 'models/Application/type';
import { application, APPLICATION_LIST_LIMIT } from 'repositories/application';
import { AvailableApplication, Device, DeviceDetail } from 'models/Device/type';
import { DeviceDomain } from 'models/Device';
import { NotifyDomain } from 'models/Notify';
import { ApplicationError } from 'models/Error';
import { Failure, Result, Success } from 'types';
import { APIDomain } from 'models/API';
import { ProvisioningProfileRole } from 'models/Application/enum';
import compareVersions from 'compare-versions';
import { DeviceFamilyType } from 'models/Device/enum';
import { localeIds } from 'locals';

export class ApplicationDomain {
  private readonly ownerType: 'users' | 'organizations';
  private readonly ownerName: string;
  private readonly bundleIdentifier: string;
  private readonly revision: string;
  private readonly deviceDomain: DeviceDomain;
  private readonly notifyDomain: NotifyDomain;

  constructor(
    ownerType: 'users' | 'organizations',
    ownerName: string,
    bundleIdentifier: string,
    revision: string,
    device?: Device
  ) {
    this.ownerType = ownerType;
    this.ownerName = ownerName;
    this.bundleIdentifier = bundleIdentifier;
    this.revision = revision;
    this.deviceDomain = new DeviceDomain(device);
    this.notifyDomain = NotifyDomain.getInstance();
  }

  async install(
    installSessionToken: string,
    overwrite: boolean,
    application: Application
  ): Promise<Result<undefined, ApplicationError>> {
    if (
      !this.deviceDomain.isInstalledConfigurationProfile() ||
      !this.deviceDomain.isAuthenticated()
    ) {
      return new Failure(
        new ApplicationError('required install configuration profile and login')
      );
    }

    if (ApplicationDomain.isExpiredProvisioningProfile(application)) {
      return new Failure(
        new ApplicationError(
          localeIds.appInstall.expiredProvisioningProfile.developer
        )
      );
    }

    try {
      const itmsBaseUrl = 'itms-services://?action=download-manifest';
      const baseUrl = `${APIDomain.origin()}/api/iphone/install_sessions/${installSessionToken}/plist`;
      const url = overwrite ? `${baseUrl}?overwrite_install=true` : baseUrl;
      location.href = `${itmsBaseUrl}&url=${encodeURIComponent(url)}`;

      await this.notifyDomain.subscribeDownloadIpa(
        this.deviceDomain.value?.uid
      );

      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async installSessionToken(): Promise<Result<string, ApplicationError>> {
    try {
      const response = await application.installSession(
        this.ownerType,
        this.ownerName,
        this.bundleIdentifier,
        this.revision
      );
      return new Success(response.results.session_token);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async detail(): Promise<Result<Application, ApplicationError>> {
    if (
      !this.deviceDomain.isInstalledConfigurationProfile() ||
      !this.deviceDomain.isAuthenticated()
    ) {
      return new Failure(
        new ApplicationError('required install configuration profile and login')
      );
    }

    try {
      const response = await application.show(
        this.ownerType,
        this.ownerName,
        this.bundleIdentifier,
        this.revision
      );
      return new Success(convertToApplication(response));
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async applications(
    maxRevision?: number
  ): Promise<
    Result<[AvailableApplication[], number | undefined], ApplicationError>
  > {
    if (
      !this.deviceDomain.isInstalledConfigurationProfile() ||
      !this.deviceDomain.isAuthenticated()
    ) {
      return new Failure(
        new ApplicationError('required install configuration profile and login')
      );
    }

    try {
      const response = await application.list(
        this.ownerType,
        this.ownerName,
        this.bundleIdentifier,
        maxRevision
      );
      const result = convertToAvailableApplicationList(response);
      const applications = result.filter((availableApplication) => {
        return availableApplication.revision !== parseInt(this.revision);
      });
      const nextMaxRevision =
        result.length >= APPLICATION_LIST_LIMIT && applications.length > 0
          ? applications[applications.length - 1].revision - 1
          : undefined;

      return new Success([applications, nextMaxRevision]);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static isInstallPermitted(application: Application): boolean {
    return (
      application.includeUdid ||
      application.provisioningProfile.role ===
        ProvisioningProfileRole.enterprise
    );
  }

  static isExpiredProvisioningProfile(application: Application): boolean {
    return application.provisioningProfile.expirationDate < new Date();
  }

  static isSupportedOsVersion(
    application: Application,
    deviceDetail: DeviceDetail
  ): boolean {
    return (
      compareVersions(deviceDetail.osVersion, application.minimumOsVersion) >= 0
    );
  }

  static isGreater0rEqualToIos16(deviceDetail: DeviceDetail): boolean {
    return compareVersions(deviceDetail.osVersion, '16') >= 0;
  }

  static isSupportedDevice(
    application: Application,
    deviceDetail: DeviceDetail
  ): boolean {
    // DeviceFamilyがuniversalの場合はiphone/ipad端末にインストール可能
    // DeviceFamilyがiphoneの場合はiphone/ipad端末にインストール可能
    // DeviceFamilyがipadの場合はipad端末のみインストール可能

    if (
      application.targetDeviceFamily === DeviceFamilyType.unSupported ||
      deviceDetail.deviceFamily === DeviceFamilyType.unSupported
    ) {
      return false;
    }

    if (
      application.targetDeviceFamily === DeviceFamilyType.ipad &&
      deviceDetail.deviceFamily === DeviceFamilyType.iphone
    ) {
      return false;
    }

    return true;
  }
}
